import { type TranslationKey, translate } from '../../i18n';
import { el } from '../../lib/dom';
import type { AppState, TabId } from '../../store/app';

/**
 * Pot: **NESYWAVE / ime zaslona** — DS `.app-topbar .crumb`
 * (`.crumb-eyebrow` · `.crumb-sep` · `.crumb-title`, `base.css:1025`).
 *
 * Ne `.breadcrumbs`: tista je večnivojska pot v telesu strani (sidebar →
 * izdelek → artikel). V glavi aplikacije DS predpisuje `.crumb` — dva člena,
 * mono eyebrow za izdelek in display naslov za trenutni zaslon.
 *
 * Zamenjala je velik naslov zaslona. Ta je na 390px pojedel 15 % višine in
 * je hkrati **podvajal oznako aktivnega zavihka** v spodnji vrstici — dvakrat
 * ista beseda na istem zaslonu.
 *
 * ⚠️ **Stoji v glavi, ne v telesu zaslona.** Pot je krom: pove, kje si, zato
 * mora ostati vidna tudi ob podrsanem seznamu. V `#screen` je drsela stran z
 * vsebino. Eyebrow nosi ime IZDELKA (Nesywave); logotip levo od nje je
 * znamka „nesylab" in ni ime te aplikacije.
 *
 * ⚠️ **Vidna pot ni naslov dokumenta, in `<h1>` NE sme v glavo.** Pot je
 * `aria-hidden`, ker bi sicer bralnik isto besedo prebral dvakrat; naslov
 * torej nosi ločen `<h1>`, vizualno skrit z `.nl-sr`. Ta `<h1>` mora stati
 * **znotraj `<main>`** (`#screenTitle` v `index.html`), ne ob poti: kdor
 * skoči po povezavi na vsebino ali po značkah do `main`, mora tam najti
 * naslov. Ko je bil `<h1>` v `<header>`, je bil `main` brez naslova.
 * Zato komponenta dobi dva korena — pot in naslov.
 *
 * Prvi člen NI povezava. `.crumb` je `aria-hidden`, fokusabilen element v
 * skritem poddrevesu pa je kršitev; dom aplikacije je dosegljiv prek
 * zavihka Postaje (P3).
 */

const TITLE_KEYS: Record<TabId, TranslationKey> = {
  stations: 'tab.stations',
  search: 'tab.search',
  history: 'tab.history',
  timer: 'tab.timer',
  settings: 'tab.settings',
};

/**
 * @param root    ovoj poti v glavi (`#crumbs`)
 * @param heading `<h1>` znotraj `<main>` (`#screenTitle`) — glej opombo zgoraj
 */
export function createBreadcrumbs(
  root: HTMLElement,
  heading: HTMLElement,
): { update(state: AppState): void } {
  const product = el('span', { class: 'crumb-eyebrow' });
  const separator = el('span', { class: 'crumb-sep' }, ['/']);
  const current = el('span', { class: 'crumb-title' });

  root.append(el('div', { class: 'crumb', 'aria-hidden': 'true' }, [product, separator, current]));

  // Pot je odvisna SAMO od jezika in zavihka, `update` pa teče ob vsaki
  // spremembi stanja — tudi ob vsakem utripu ICY metapodatkov. Ker vozlišča
  // ležijo v stekleni glavi, vsak zapis razveljavi njen zamegljeni sloj in
  // sili v ponoven izris. Zato pišemo le, kadar se je res kaj spremenilo.
  let lastLang: AppState['lang'] | null = null;
  let lastTab: TabId | null = null;

  return {
    update(state) {
      const { lang, tab } = state;
      if (lang === lastLang && tab === lastTab) return;
      lastLang = lang;
      lastTab = tab;

      const title = translate(lang, TITLE_KEYS[tab]);

      // Velike črke naredi DS (`text-transform`), ne niz — sicer bi bila
      // v prevodu zapisana verzalka, ki je z Intl ni mogoče vrniti nazaj.
      product.textContent = translate(lang, 'app.name');
      current.textContent = title;
      heading.textContent = title;
    },
  };
}
